import {
  type AppState,
  Auth0Provider,
  type Auth0ProviderWithConfigOptions,
} from '@auth0/auth0-react';
import { type ReactNode } from 'react';
import { getAuth0Config } from '../utils/environment';

const supportedLanguages = new Set(['en', 'sv', 'da', 'no', 'fi', 'pl']);

export const getLanguageFromLocation = (location: Location = window.location) => {
  const language =
    new URLSearchParams(location.search).get('lang') ??
    new URLSearchParams(location.search).get('language');

  if (!language) {
    return undefined;
  }

  const normalizedLanguage = language.toLowerCase().split('-')[0];

  return supportedLanguages.has(normalizedLanguage) ? normalizedLanguage : undefined;
};

type AuthProviderProps = {
  domain?: string;
  clientId?: string;
} & Omit<Auth0ProviderWithConfigOptions, 'domain' | 'clientId' | 'audience' | 'redirectUrl'>;
/**
 * Wrap your application with this component to get access to the profile and use Auth0-authentication.
 * If cachelocation is localstorage or undefined, will cache accessToken in localStorage for 1 minute.
 * After the minute is passed, the framework will automatically get new instance of token. This is done to ensure that the
 * token is still valid (not expired and user has not signed out from other locations).
 * @param param0 { domain, clientId } If not specified, the domain and clientId will be taken from the environment.
 * @returns
 */
export const AuthProvider = ({ children, domain, clientId, ...rest }: AuthProviderProps) => {
  const auth0Config = getAuth0Config();
  const audience = 'https://user.stenarecycling.cloud/api';

  const langFromQuery = getLanguageFromLocation();

  return (
    <Auth0Provider
      domain={domain ?? auth0Config.domain}
      clientId={clientId ?? auth0Config.clientId}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      authorizationParams={{
        audience,
        redirect_uri: window.location.origin,
        langFromQuery,
      }}
      useFormData={false}
      {...rest}
    >
      {children}
    </Auth0Provider>
  );
};

type Auth0ProviderWithHistoryProps = {
  children: ReactNode;
};

export const Auth0ProviderWithHistory = ({ children }: Auth0ProviderWithHistoryProps) => {
  const onRedirectCallback = (appState: AppState | undefined) => {
    let url = appState?.returnTo ?? window.location.pathname;

    if (url.includes('?code')) {
      //Remove query parameters from the url
      url = url.split('?')[0];
    }
    window.location.replace(url);
  };

  return (
    <AuthProvider cacheLocation="localstorage" onRedirectCallback={onRedirectCallback}>
      {children}
    </AuthProvider>
  );
};
